/**
 * The data-freshness badge (docs/07-gui-proposal §2): it is ALWAYS on screen, because old numbers
 * must never look like new ones.
 *
 * Pure: the answer depends only on what the API reported and on the business day passed in.
 */
export interface Freshness {
  readonly lastScrapeAt: string | null;
  readonly dataFrom: string | null;
  readonly dataTo: string | null;
  readonly missingDays: number;
}

export type FreshnessTone = 'ok' | 'late' | 'lost' | 'unknown';

export interface FreshnessState {
  readonly tone: FreshnessTone;
  readonly text: string;
}

/**
 * `ok` — the scrape has reached today; `late` — it stopped yesterday (one missed run);
 * `lost` — two days or more without the source; `unknown` — nothing scraped at all yet.
 */
export function freshnessState(freshness: Freshness | null, today: string): FreshnessState {
  if (freshness === null || freshness.dataTo === null)
    return { tone: 'unknown', text: 'Chưa có dữ liệu' };
  const daysBehind = Math.round(
    (Date.parse(`${today}T00:00:00Z`) - Date.parse(`${freshness.dataTo}T00:00:00Z`)) / 86_400_000,
  );
  if (daysBehind <= 0)
    return { tone: 'ok', text: `Cập nhật ${scrapeClock(freshness.lastScrapeAt)}` };
  if (daysBehind === 1) return { tone: 'late', text: 'Trễ 1 ngày' };
  return { tone: 'lost', text: `Mất nguồn ${daysBehind} ngày` };
}

/**
 * "20:00 hôm nay" — the wall clock of the last scrape, the way the approved mockup writes it
 * (docs/13-tasks/01-ui-chrome.md#13 §4.A6). The API already spells the instant in the business zone
 * (packages/shared/src/vnTime.js), so the text is READ, never rebuilt through a Date: doing that
 * would shift the clock by the reader's own zone. Nothing scraped yet ⇒ no clock to claim.
 */
function scrapeClock(lastScrapeAt: string | null): string {
  if (typeof lastScrapeAt !== 'string' || lastScrapeAt.length < 16) return 'tới hôm nay';
  return `${lastScrapeAt.slice(11, 16)} hôm nay`;
}

/** The one line under the badge: where the data starts and how many days are missing. */
export function coverageText(freshness: Freshness | null): string | null {
  if (freshness === null || freshness.dataFrom === null || freshness.dataTo === null) return null;
  const missing = freshness.missingDays === 0 ? 'đủ ngày' : `thiếu ${freshness.missingDays} ngày`;
  return `${freshness.dataFrom} → ${freshness.dataTo} · ${missing}`;
}
